import React, { useState, useMemo, useEffect } from "react";
import { doc, updateDoc, setDoc, addDoc, deleteDoc, collection, arrayUnion, arrayRemove, query, where, orderBy, getDocs } from "firebase/firestore";
import { Button, Switch, Chip, CircularProgress, TextField, FormControlLabel, Checkbox, FormGroup, MenuItem, Tooltip } from "@mui/material";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import AddIcon from "@mui/icons-material/Add";
import { useUserAuth } from "../context/UserAuthContext";
import useIsMobile from "../hooks/useIsMobile";
import { db } from "../firebase";
import { HUBSPOT_INSTALL_URL, getSlackInstallUrl, FUNCTIONS } from "../config";

const OBJECT_TYPE_OPTIONS = ["line_items", "deals", "contacts", "companies", "quotes"];

const SYSTEM_PROMPT = [
    "You are a HubSpot quoting assistant integrated with Slack.",
    "When a user requests a quote:",
    "1. Search HubSpot for the relevant deal, contact, and company.",
    "2. Look up the requested products/line items and their current pricing.",
    "3. Create line items with the correct quantities and prices.",
    "4. Assemble a professional quote linked to the deal, contact, and company.",
    "5. Apply the configured quote template, expiration, e-sign, and payment settings.",
    "6. Return a summary with the quote link.",
    "Always confirm ambiguous product names or quantities before creating the quote.",
    "If a deal does not exist yet, create one using the configured pipeline, stage, and owner defaults.",
].join("\n");

const AGENT_DEFAULTS = {
    name: "",
    instructions: SYSTEM_PROMPT,
    enabled: false,
    esignEnabled: false,
    showCountersignatureBox: false,
    paymentEnabled: false,
    storePaymentMethod: false,
    defaultDealPipeline: "",
    defaultDealStage: "",
    defaultDealOwner: "",
    quoteTemplate: "",
    quoteExpirationDays: 30,
    quoteHsDomain: "",
    setQuoteSenderData: false,
    senderFirstName: "",
    senderLastName: "",
    senderEmail: "",
    senderJobTitle: "",
    senderPhone: "",
    senderCompanyName: "",
    senderCompanyAddress: "",
    senderAddress2: "",
    senderCity: "",
    senderZip: "",
    senderState: "",
    senderCountry: "",
    senderDomain: "",
    quoteSummaryMetric: "TCV",
    needsApproval: false,
    quoteApprovalInstructions: "",
    planLineItemsInstructions: "",
    objectTypesToFetch: [],
    requiredFields: [],
    quoteLog: [],
};

const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#825ee7" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#825ee7" },
};

const SectionHeader = ({ children }) => (
    <div style={{ fontSize: "13px", fontWeight: 600, color: "#825ee7", textTransform: "uppercase", letterSpacing: "0.05em", margin: "24px 0 12px 0", borderBottom: "1px solid #f3f4f6", paddingBottom: "8px" }}>
        {children}
    </div>
);

const FieldRow = ({ children, isMobile }) => (
    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "12px", marginBottom: "12px" }}>
        {children}
    </div>
);

const Dashboard = () => {
    const { user, orgId, orgData, orgLoading, isAdmin, hubspotConnectionId, slackConnectionId } = useUserAuth();
    const isMobile = useIsMobile();

    const [showAgentForm, setShowAgentForm] = useState(false);
    const [editingAgentId, setEditingAgentId] = useState(null); // null = creating new
    const [form, setForm] = useState({ ...AGENT_DEFAULTS });
    const [saving, setSaving] = useState(false);
    const [statsRange, setStatsRange] = useState(30);
    const [hubspotOptions, setHubspotOptions] = useState(null);
    const [loadingOptions, setLoadingOptions] = useState(false);
    const [recommendations, setRecommendations] = useState([]);
    const [reqFieldObjType, setReqFieldObjType] = useState("");
    const [reqFieldProp, setReqFieldProp] = useState("");
    const [threadFeedback, setThreadFeedback] = useState({});

    // Fetch chatThreads feedback grouped by agentId
    useEffect(() => {
        if (!user?.uid) return;
        const q = query(
            collection(db, "chatThreads"),
            where("userId", "==", user.uid)
        );
        getDocs(q).then((snap) => {
            const grouped = {};
            snap.docs.forEach((d) => {
                const data = d.data();
                const aid = data.agentId;
                if (!aid) return;
                if (!grouped[aid]) grouped[aid] = [];
                const fb = data.feedback || {};
                let ups = 0, downs = 0;
                Object.values(fb).forEach((v) => {
                    if (v === "up") ups++;
                    else if (v === "down") downs++;
                });
                const createdAt = data.createdAt?.toDate?.()
                    ? data.createdAt.toDate()
                    : data.createdAt?.seconds
                        ? new Date(data.createdAt.seconds * 1000)
                        : new Date(data.createdAt);
                grouped[aid].push({ createdAt, ups, downs });
            });
            setThreadFeedback(grouped);
        }).catch((err) => console.error("Failed to fetch thread feedback:", err));
    }, [user]);

    if (orgLoading || !orgData) {
        return (
            <div style={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "400px" }}>
                <CircularProgress style={{ color: "#825ee7" }} />
            </div>
        );
    }

    const hubspot = orgData?.hubspotConnection || {};
    const slack = orgData?.slackConnection || {};
    const agentsMap = orgData?.agents || {};
    const agentEntries = Object.entries(agentsMap); // [[id, data], ...]
    const bothConnected = hubspot.connected && slack.connected;
    const neitherConnected = !hubspot.connected && !slack.connected;

    const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

    const toggleObjectType = (type) => {
        setForm((prev) => {
            const current = prev.objectTypesToFetch || [];
            return {
                ...prev,
                objectTypesToFetch: current.includes(type)
                    ? current.filter((t) => t !== type)
                    : [...current, type],
            };
        });
    };

    const openAgentForm = (agentId, existing) => {
        if (existing) {
            const merged = { ...AGENT_DEFAULTS };
            for (const key of Object.keys(AGENT_DEFAULTS)) {
                if (existing[key] !== undefined) merged[key] = existing[key];
            }
            setForm(merged);
        } else {
            setForm({ ...AGENT_DEFAULTS });
        }
        setEditingAgentId(agentId);
        setShowAgentForm(true);
        setRecommendations([]);

        // Fetch pending prompt recommendations for this agent
        if (agentId) {
            const recsRef = collection(db, "agents", agentId, "promptRecommendations");
            const q = query(recsRef, where("status", "==", "pending"), orderBy("createdAt", "desc"));
            getDocs(q)
                .then((snap) => setRecommendations(snap.docs.map((d) => ({ id: d.id, ...d.data() }))))
                .catch(console.error);
        }

        // Fetch HubSpot options for dropdowns (pipelines, owners, templates, domains)
        if (hubspot.connected && hubspotConnectionId && !hubspotOptions) {
            setLoadingOptions(true);
            user.getIdToken().then((token) =>
                fetch(FUNCTIONS.getHubspotOptions, {
                    method: "POST",
                    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
                    body: JSON.stringify({ portalId: hubspotConnectionId }),
                })
            )
                .then((res) => res.json())
                .then((data) => {
                    if (!data.error) setHubspotOptions(data);
                })
                .catch(console.error)
                .finally(() => setLoadingOptions(false));
        }
    };

    const handleApproveRec = async (rec) => {
        if (!editingAgentId) return;
        setForm((prev) => ({
            ...prev,
            planLineItemsInstructions: prev.planLineItemsInstructions
                ? `${prev.planLineItemsInstructions}\n${rec.recommendation}`
                : rec.recommendation,
        }));
        await updateDoc(doc(db, "agents", editingAgentId, "promptRecommendations", rec.id), { status: "approved" });
        setRecommendations((prev) => prev.filter((r) => r.id !== rec.id));
    };

    const handleRejectRec = async (rec) => {
        if (!editingAgentId) return;
        await updateDoc(doc(db, "agents", editingAgentId, "promptRecommendations", rec.id), { status: "rejected" });
        setRecommendations((prev) => prev.filter((r) => r.id !== rec.id));
    };

    const handleSaveAgent = async () => {
        if (!orgId) return;
        setSaving(true);
        const now = new Date().toISOString();
        const isEdit = !!editingAgentId;
        const { quoteLog, ...formWithoutLog } = form;
        const agentData = {
            ...formWithoutLog,
            orgId,
            updatedAt: now,
            ...(!isEdit && { createdAt: now, quoteLog: [] }),
        };

        // If this agent is enabled, disable all others
        if (agentData.enabled) {
            const disablePromises = agentEntries
                .filter(([id, a]) => id !== editingAgentId && a.enabled)
                .map(([id]) => updateDoc(doc(db, "agents", id), { enabled: false, updatedAt: now }));
            await Promise.all(disablePromises);
        }

        if (isEdit) {
            // Update existing agent doc
            await updateDoc(doc(db, "agents", editingAgentId), agentData);
        } else {
            // Create new agent doc + add ID to org's agentIds array
            const agentRef = await addDoc(collection(db, "agents"), agentData);
            await updateDoc(doc(db, "organizations", orgId), {
                agentIds: arrayUnion(agentRef.id),
            });
        }

        setSaving(false);
        setShowAgentForm(false);
        setEditingAgentId(null);
    };

    const handleDisconnect = async (connectionType) => {
        if (!orgId) return;
        if (connectionType === "hubspot" && hubspotConnectionId) {
            await updateDoc(doc(db, "hubspotConnections", hubspotConnectionId), { connected: false });
        } else if (connectionType === "slack" && slackConnectionId) {
            await updateDoc(doc(db, "slackConnections", slackConnectionId), { connected: false });
        }
    };

    const handleToggleAgent = async (agentId) => {
        if (!orgId) return;
        const agent = agentsMap[agentId];
        if (!agent) return;
        const now = new Date().toISOString();
        const newEnabled = !agent.enabled;

        // If enabling this agent, disable all others
        if (newEnabled) {
            const disablePromises = agentEntries
                .filter(([id, a]) => id !== agentId && a.enabled)
                .map(([id]) => updateDoc(doc(db, "agents", id), { enabled: false, updatedAt: now }));
            await Promise.all(disablePromises);
        }

        await updateDoc(doc(db, "agents", agentId), { enabled: newEnabled, updatedAt: now });
    };

    const handleDeleteAgent = async (agentId) => {
        if (!orgId) return;
        await deleteDoc(doc(db, "agents", agentId));
        await updateDoc(doc(db, "organizations", orgId), {
            agentIds: arrayRemove(agentId),
        });
        if (editingAgentId === agentId) {
            setShowAgentForm(false);
            setEditingAgentId(null);
        }
    };

    /* ── Empty state ── */
    if (neitherConnected && agentEntries.length === 0) {
        return (
            <div>
                <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
                    <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Dashboard</h1>
                    {isAdmin && <Chip label="Admin" size="small" sx={{ backgroundColor: "#f0ebfc", color: "#825ee7", fontWeight: 600, fontSize: "11px" }} />}
                </div>
                {isAdmin ? (
                    <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: isMobile ? "32px 16px" : "64px 40px", textAlign: "center" }}>
                        <div style={{ width: "72px", height: "72px", borderRadius: "50%", backgroundColor: "#f0ebfc", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 24px" }}>
                            <RocketLaunchIcon style={{ fontSize: "36px", color: "#825ee7" }} />
                        </div>
                        <h2 style={{ fontSize: "20px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 8px 0" }}>Get started</h2>
                        <p style={{ color: "#6b7280", fontSize: "15px", maxWidth: "420px", margin: "0 auto 32px", lineHeight: 1.6 }}>
                            Connect your HubSpot and Slack accounts to set up your quoting agent. It only takes a minute.
                        </p>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                            <Button variant="contained" onClick={() => { window.location.href = HUBSPOT_INSTALL_URL; }} style={{ backgroundColor: "#ff7a59", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", padding: "10px 24px" }}>
                                Connect HubSpot
                            </Button>
                            <Button variant="contained" onClick={() => { window.location.href = getSlackInstallUrl(); }} style={{ backgroundColor: "#4A154B", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", padding: "10px 24px" }}>
                                Connect Slack
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: isMobile ? "32px 16px" : "64px 40px", textAlign: "center" }}>
                        <p style={{ color: "#6b7280", fontSize: "15px", margin: 0 }}>
                            Your admin hasn't connected any integrations yet. Please check back later.
                        </p>
                    </div>
                )}
            </div>
        );
    }

    /* ── Inline agent form ── */
    const renderAgentForm = () => (
        <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: isMobile ? "16px" : "24px", marginBottom: "16px" }}>
            <h4 style={{ fontSize: "15px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 4px 0" }}>
                {editingAgentId ? "Edit Agent" : "Create Agent"}
            </h4>

            {/* General */}
            <SectionHeader>General</SectionHeader>
            <TextField label="Agent Name" placeholder="e.g. Quoting Agent" value={form.name} onChange={(e) => setField("name", e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
            <TextField label="Instructions" placeholder="Add any extra instructions for how this agent should handle quoting requests..." value={form.planLineItemsInstructions} onChange={(e) => setField("planLineItemsInstructions", e.target.value)} fullWidth multiline minRows={3} size="small" sx={{ mb: 1.5 }} helperText="Custom instructions appended to the agent's built-in quoting behavior" />
            {recommendations.length > 0 && (
                <div style={{ marginBottom: "12px" }}>
                    <div style={{ fontSize: "11px", fontWeight: 600, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: "6px" }}>
                        Prompt Recommendations
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                        {recommendations.map((rec) => (
                            <Tooltip key={rec.id} title={rec.originalFeedback ? `Feedback: "${rec.originalFeedback}"` : ""} arrow>
                                <Chip
                                    label={rec.recommendation}
                                    size="small"
                                    onClick={() => handleApproveRec(rec)}
                                    onDelete={() => handleRejectRec(rec)}
                                    sx={{
                                        backgroundColor: "#f0ebfc",
                                        color: "#825ee7",
                                        fontWeight: 500,
                                        fontSize: "12px",
                                        maxWidth: "100%",
                                        height: "auto",
                                        "& .MuiChip-label": { whiteSpace: "normal", padding: "6px 8px" },
                                        "& .MuiChip-deleteIcon": { color: "#9ca3af", "&:hover": { color: "#dc2626" } },
                                        "&:hover": { backgroundColor: "#e4dbf9" },
                                    }}
                                />
                            </Tooltip>
                        ))}
                    </div>
                    <div style={{ fontSize: "10px", color: "#9ca3af", marginTop: "4px" }}>Click to approve and add to instructions. Click ✕ to reject.</div>
                </div>
            )}
            <FormControlLabel control={<Switch checked={form.enabled} onChange={(e) => setField("enabled", e.target.checked)} size="small" sx={switchSx} />} label="Enabled" />

            {/* E-Sign & Payment */}
            <SectionHeader>E-Sign &amp; Payment</SectionHeader>
            <FormControlLabel control={<Switch checked={form.esignEnabled} onChange={(e) => setField("esignEnabled", e.target.checked)} size="small" sx={switchSx} />} label="E-sign enabled" />
            <FormControlLabel control={<Switch checked={form.showCountersignatureBox} onChange={(e) => setField("showCountersignatureBox", e.target.checked)} size="small" sx={switchSx} />} label="Show countersignature box" sx={{ display: "block" }} />
            <FormControlLabel control={<Switch checked={form.paymentEnabled} onChange={(e) => setField("paymentEnabled", e.target.checked)} size="small" sx={switchSx} />} label="Payment enabled" sx={{ display: "block" }} />
            <FormControlLabel control={<Switch checked={form.storePaymentMethod} onChange={(e) => setField("storePaymentMethod", e.target.checked)} size="small" sx={switchSx} />} label="Store payment method at checkout" sx={{ display: "block" }} />

            {/* Deal Defaults */}
            <SectionHeader>Deal Defaults</SectionHeader>
            <TextField
                label="Default Deal Pipeline"
                value={form.defaultDealPipeline}
                onChange={(e) => { setField("defaultDealPipeline", e.target.value); setField("defaultDealStage", ""); }}
                fullWidth size="small" select
                disabled={loadingOptions}
                sx={{ mb: 1.5 }}
            >
                <MenuItem value=""><em>{loadingOptions ? "Loading..." : "None"}</em></MenuItem>
                {(hubspotOptions?.pipelines || []).map((p) => (
                    <MenuItem key={p.id} value={p.id}>{p.label}</MenuItem>
                ))}
            </TextField>
            <FieldRow isMobile={isMobile}>
                <TextField
                    label="Default Deal Stage"
                    value={form.defaultDealStage}
                    onChange={(e) => setField("defaultDealStage", e.target.value)}
                    size="small" select
                    disabled={loadingOptions || !form.defaultDealPipeline}
                >
                    <MenuItem value=""><em>{!form.defaultDealPipeline ? "Select pipeline first" : "None"}</em></MenuItem>
                    {(() => {
                        const pipeline = (hubspotOptions?.pipelines || []).find((p) => p.id === form.defaultDealPipeline);
                        return (pipeline?.stages || [])
                            .sort((a, b) => a.displayOrder - b.displayOrder)
                            .map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>);
                    })()}
                </TextField>
                <TextField
                    label="Default Deal Owner"
                    value={form.defaultDealOwner}
                    onChange={(e) => setField("defaultDealOwner", e.target.value)}
                    size="small" select
                    disabled={loadingOptions}
                >
                    <MenuItem value=""><em>{loadingOptions ? "Loading..." : "None"}</em></MenuItem>
                    {(hubspotOptions?.owners || []).map((o) => (
                        <MenuItem key={o.ownerId} value={o.ownerId}>{`${o.firstName || ""} ${o.lastName || ""}`.trim() || o.email}</MenuItem>
                    ))}
                </TextField>
            </FieldRow>

            {/* Quote Settings */}
            <SectionHeader>Quote Settings</SectionHeader>
            <FieldRow isMobile={isMobile}>
                <TextField
                    label="Quote Template"
                    value={form.quoteTemplate}
                    onChange={(e) => setField("quoteTemplate", e.target.value)}
                    size="small" select
                    disabled={loadingOptions}
                >
                    <MenuItem value=""><em>{loadingOptions ? "Loading..." : "None"}</em></MenuItem>
                    {(hubspotOptions?.quoteTemplates || []).map((t) => (
                        <MenuItem key={t.id} value={t.id}>{t.properties?.hs_name || `Template ${t.id}`}</MenuItem>
                    ))}
                </TextField>
                <TextField label="Quote Expiration Days" value={form.quoteExpirationDays} onChange={(e) => setField("quoteExpirationDays", e.target.value)} size="small" type="number" />
            </FieldRow>
            <FieldRow isMobile={isMobile}>
                {(hubspotOptions?.domains || []).length > 0 ? (
                    <TextField
                        label="Quote HS Domain"
                        value={form.quoteHsDomain}
                        onChange={(e) => setField("quoteHsDomain", e.target.value)}
                        size="small" select
                    >
                        <MenuItem value=""><em>None</em></MenuItem>
                        {hubspotOptions.domains.map((d) => (
                            <MenuItem key={d.id} value={d.domain}>{d.domain}</MenuItem>
                        ))}
                    </TextField>
                ) : (
                    <TextField label="Quote HS Domain" value={form.quoteHsDomain} onChange={(e) => setField("quoteHsDomain", e.target.value)} size="small" disabled={loadingOptions} placeholder={loadingOptions ? "Loading..." : ""} />
                )}
                <TextField label="Quote Summary Metric" value={form.quoteSummaryMetric} onChange={(e) => setField("quoteSummaryMetric", e.target.value)} size="small" select>
                    <MenuItem value="TCV">TCV (Total Contract Value)</MenuItem>
                    <MenuItem value="ACV">ACV (Annual Contract Value)</MenuItem>
                    <MenuItem value="ARR">ARR (Annual Recurring Revenue)</MenuItem>
                </TextField>
            </FieldRow>

            {/* Sender Data */}
            <SectionHeader>Sender Data</SectionHeader>
            <FormControlLabel control={<Switch checked={form.setQuoteSenderData} onChange={(e) => setField("setQuoteSenderData", e.target.checked)} size="small" sx={switchSx} />} label="Set quote sender data" sx={{ mb: 1 }} />
            {form.setQuoteSenderData && (
                <div style={{ padding: "12px", backgroundColor: "#fafafa", borderRadius: "8px", border: "1px solid #f3f4f6" }}>
                    <FieldRow isMobile={isMobile}>
                        <TextField label="First Name" value={form.senderFirstName} onChange={(e) => setField("senderFirstName", e.target.value)} size="small" />
                        <TextField label="Last Name" value={form.senderLastName} onChange={(e) => setField("senderLastName", e.target.value)} size="small" />
                    </FieldRow>
                    <FieldRow isMobile={isMobile}>
                        <TextField label="Email" value={form.senderEmail} onChange={(e) => setField("senderEmail", e.target.value)} size="small" />
                        <TextField label="Job Title" value={form.senderJobTitle} onChange={(e) => setField("senderJobTitle", e.target.value)} size="small" />
                    </FieldRow>
                    <FieldRow isMobile={isMobile}>
                        <TextField label="Phone" value={form.senderPhone} onChange={(e) => setField("senderPhone", e.target.value)} size="small" />
                        <TextField label="Company Name" value={form.senderCompanyName} onChange={(e) => setField("senderCompanyName", e.target.value)} size="small" />
                    </FieldRow>
                    <TextField label="Company Address" value={form.senderCompanyAddress} onChange={(e) => setField("senderCompanyAddress", e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                    <TextField label="Address Line 2" value={form.senderAddress2} onChange={(e) => setField("senderAddress2", e.target.value)} fullWidth size="small" sx={{ mb: 1.5 }} />
                    <FieldRow isMobile={isMobile}>
                        <TextField label="City" value={form.senderCity} onChange={(e) => setField("senderCity", e.target.value)} size="small" />
                        <TextField label="State" value={form.senderState} onChange={(e) => setField("senderState", e.target.value)} size="small" />
                    </FieldRow>
                    <FieldRow isMobile={isMobile}>
                        <TextField label="Zip" value={form.senderZip} onChange={(e) => setField("senderZip", e.target.value)} size="small" />
                        <TextField label="Country" value={form.senderCountry} onChange={(e) => setField("senderCountry", e.target.value)} size="small" />
                    </FieldRow>
                    <TextField label="Domain" value={form.senderDomain} onChange={(e) => setField("senderDomain", e.target.value)} fullWidth size="small" />
                </div>
            )}

            {/* Advanced */}
            <SectionHeader>Advanced</SectionHeader>
            <FormControlLabel control={<Switch checked={form.needsApproval} onChange={(e) => setField("needsApproval", e.target.checked)} size="small" sx={switchSx} />} label="Needs approval" sx={{ display: "block", mb: 1.5 }} />
            {form.needsApproval && (
                <TextField label="Quote Approval Instructions" placeholder="e.g. Require manager approval for quotes over $10,000..." value={form.quoteApprovalInstructions} onChange={(e) => setField("quoteApprovalInstructions", e.target.value)} fullWidth multiline minRows={2} size="small" sx={{ mb: 1.5 }} helperText="Instructions for how quote approval should be handled" />
            )}

            <div style={{ fontSize: "13px", fontWeight: 500, color: "#374151", marginBottom: "8px" }}>Object Types to Fetch</div>
            <FormGroup row>
                {OBJECT_TYPE_OPTIONS.map((type) => (
                    <FormControlLabel
                        key={type}
                        control={
                            <Checkbox
                                checked={(form.objectTypesToFetch || []).includes(type)}
                                onChange={() => toggleObjectType(type)}
                                size="small"
                                sx={{ "&.Mui-checked": { color: "#825ee7" } }}
                            />
                        }
                        label={type.replace("_", " ")}
                        sx={{ "& .MuiFormControlLabel-label": { fontSize: "13px" } }}
                    />
                ))}
            </FormGroup>

            {/* Required Fields */}
            <div style={{ fontSize: "13px", fontWeight: 500, color: "#374151", marginTop: "16px", marginBottom: "8px" }}>Required Fields</div>
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "8px", alignItems: "flex-end" }}>
                <TextField
                    label="Object Type"
                    value={reqFieldObjType}
                    onChange={(e) => { setReqFieldObjType(e.target.value); setReqFieldProp(""); }}
                    size="small" select
                    disabled={loadingOptions}
                    sx={{ minWidth: isMobile ? 0 : 150, flex: isMobile ? 1 : undefined }}
                >
                    <MenuItem value=""><em>Select...</em></MenuItem>
                    {["deals", "contacts", "companies", "line_items", "quotes"].map((t) => (
                        <MenuItem key={t} value={t}>{t.replace("_", " ")}</MenuItem>
                    ))}
                </TextField>
                <TextField
                    label="Property"
                    value={reqFieldProp}
                    onChange={(e) => setReqFieldProp(e.target.value)}
                    size="small" select
                    disabled={!reqFieldObjType || loadingOptions}
                    sx={{ minWidth: isMobile ? 0 : 200, flex: 1 }}
                >
                    <MenuItem value=""><em>{!reqFieldObjType ? "Select object type first" : loadingOptions ? "Loading..." : "Select..."}</em></MenuItem>
                    {(hubspotOptions?.properties?.[reqFieldObjType] || []).map((p) => (
                        <MenuItem key={p.name} value={p.name}>{p.label} ({p.name})</MenuItem>
                    ))}
                </TextField>
                <Button
                    size="small"
                    variant="outlined"
                    disabled={!reqFieldObjType || !reqFieldProp || (form.requiredFields || []).includes(`${reqFieldObjType}.${reqFieldProp}`)}
                    onClick={() => {
                        const field = `${reqFieldObjType}.${reqFieldProp}`;
                        setField("requiredFields", [...(form.requiredFields || []), field]);
                        setReqFieldProp("");
                    }}
                    sx={{ color: "#825ee7", borderColor: "#ddd6fe", textTransform: "none", fontWeight: 500, borderRadius: "6px", whiteSpace: "nowrap", "&:hover": { backgroundColor: "#f5f3ff" } }}
                >
                    Add
                </Button>
            </div>
            {(form.requiredFields || []).length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px", marginBottom: "8px" }}>
                    {(form.requiredFields || []).map((f) => (
                        <Chip
                            key={f}
                            label={f}
                            size="small"
                            onDelete={() => setField("requiredFields", (form.requiredFields || []).filter((x) => x !== f))}
                            sx={{ backgroundColor: "#f0ebfc", color: "#825ee7", fontWeight: 500, fontSize: "12px", "& .MuiChip-deleteIcon": { color: "#9ca3af", "&:hover": { color: "#dc2626" } } }}
                        />
                    ))}
                </div>
            )}
            <div style={{ fontSize: "11px", color: "#9ca3af", marginBottom: "4px" }}>Fields the agent must collect before creating a quote.</div>

            {/* Actions */}
            <div style={{ display: "flex", gap: "8px", marginTop: "24px", borderTop: "1px solid #f3f4f6", paddingTop: "16px" }}>
                <Button variant="contained" onClick={handleSaveAgent} disabled={saving || !form.name.trim()} style={{ backgroundColor: "#825ee7", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px" }}>
                    {saving ? "Saving..." : "Save"}
                </Button>
                <Button variant="outlined" onClick={() => { setShowAgentForm(false); setEditingAgentId(null); }} sx={{ color: "#6b7280", borderColor: "#d1d5db", textTransform: "none", fontWeight: 500, borderRadius: "8px", "&:hover": { backgroundColor: "#f9fafb", borderColor: "#d1d5db" } }}>
                    Cancel
                </Button>
            </div>
        </div>
    );

    /* ── Inline agent analytics (rendered inside each agent card) ── */
    const renderInlineStats = (agentId) => {
        const entries = threadFeedback[agentId] || [];

        // Totals across all threads for this agent
        const totalUps = entries.reduce((s, e) => s + e.ups, 0);
        const totalDowns = entries.reduce((s, e) => s + e.downs, 0);
        const total = totalUps + totalDowns;
        const positivePct = total > 0 ? Math.round((totalUps / total) * 100) : null;

        const now = new Date();
        const cutoff = new Date(now);
        cutoff.setDate(cutoff.getDate() - statsRange);
        const filtered = entries.filter((e) => e.createdAt && !isNaN(e.createdAt) && e.createdAt >= cutoff);
        const filteredUps = filtered.reduce((s, e) => s + e.ups, 0);
        const filteredDowns = filtered.reduce((s, e) => s + e.downs, 0);

        // Group by day with up/down breakdown
        const dayCounts = {};
        for (let d = new Date(cutoff); d <= now; d.setDate(d.getDate() + 1)) {
            dayCounts[d.toISOString().slice(0, 10)] = { up: 0, down: 0 };
        }
        filtered.forEach((e) => {
            const key = e.createdAt.toISOString().slice(0, 10);
            if (key in dayCounts) {
                dayCounts[key].up += e.ups;
                dayCounts[key].down += e.downs;
            }
        });
        const days = Object.entries(dayCounts);
        const maxCount = Math.max(1, ...days.map(([, c]) => c.up + c.down));

        const ringSize = 56;
        const stroke = 5;
        const radius = (ringSize - stroke) / 2;
        const circumference = 2 * Math.PI * radius;
        const positiveOffset = positivePct !== null ? circumference * (1 - positivePct / 100) : circumference;

        return (
            <div style={{ borderTop: "1px solid #f3f4f6", marginTop: "16px", paddingTop: "16px", overflow: "hidden", minWidth: 0 }}>
                <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", justifyContent: "space-between", alignItems: isMobile ? "stretch" : "center", gap: isMobile ? "12px" : "0", marginBottom: "12px" }}>
                    <div style={{ display: "flex", gap: isMobile ? "6px" : "12px", flexWrap: "wrap" }}>
                        {[
                            { label: "Total", value: total, color: "#825ee7" },
                            { label: `${statsRange}d`, value: filteredUps + filteredDowns, color: "#6366f1" },
                            { label: "Positive", value: totalUps, color: "#16a34a" },
                            { label: "Negative", value: totalDowns, color: "#dc2626" },
                        ].map((kpi) => (
                            <div key={kpi.label} style={{ textAlign: "center", padding: isMobile ? "4px 6px" : "6px 10px", backgroundColor: "#fafafa", borderRadius: "6px", minWidth: isMobile ? 0 : "56px", flex: isMobile ? "1 1 0" : undefined }}>
                                <div style={{ fontSize: isMobile ? "16px" : "18px", fontWeight: 700, color: kpi.color }}>{kpi.value}</div>
                                <div style={{ fontSize: isMobile ? "9px" : "10px", fontWeight: 500, color: "#6b7280", textTransform: "uppercase" }}>{kpi.label}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px", justifyContent: isMobile ? "flex-start" : undefined }}>
                        <div style={{ textAlign: "center" }}>
                            <div style={{ position: "relative", width: ringSize, height: ringSize }}>
                                <svg width={ringSize} height={ringSize} style={{ transform: "rotate(-90deg)" }}>
                                    <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke="#f3f4f6" strokeWidth={stroke} />
                                    <circle cx={ringSize / 2} cy={ringSize / 2} r={radius} fill="none" stroke={positivePct !== null ? (positivePct >= 70 ? "#16a34a" : positivePct >= 40 ? "#f59e0b" : "#dc2626") : "#e5e7eb"} strokeWidth={stroke} strokeDasharray={circumference} strokeDashoffset={positiveOffset} strokeLinecap="round" style={{ transition: "stroke-dashoffset 0.4s" }} />
                                </svg>
                                <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "13px", fontWeight: 700, color: "#1a1a1a" }}>
                                    {positivePct !== null ? `${positivePct}%` : "—"}
                                </div>
                            </div>
                            <div style={{ fontSize: "9px", color: "#9ca3af", marginTop: "2px" }}>{total} rated</div>
                        </div>
                        <TextField
                            select size="small" value={statsRange}
                            onChange={(e) => setStatsRange(Number(e.target.value))}
                            sx={{ width: isMobile ? 90 : 120, "& .MuiInputBase-input": { fontSize: "12px", padding: "4px 8px" } }}
                        >
                            <MenuItem value={7}>7 days</MenuItem>
                            <MenuItem value={30}>30 days</MenuItem>
                            <MenuItem value={90}>90 days</MenuItem>
                        </TextField>
                    </div>
                </div>
                <div style={{ display: "flex", alignItems: "flex-end", gap: "2px", height: "60px", minWidth: 0, overflow: "hidden" }}>
                    {days.map(([day, counts]) => {
                        const dayTotal = counts.up + counts.down;
                        const pct = (v) => `${(v / maxCount) * 100}%`;
                        return (
                            <div key={day} style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", minWidth: 0 }} title={`${day}: ${dayTotal}`}>
                                <div style={{ width: "100%", maxWidth: "14px", display: "flex", flexDirection: "column", justifyContent: "flex-end", height: `${(dayTotal / maxCount) * 100}%`, minHeight: dayTotal > 0 ? "3px" : "1px" }}>
                                    {counts.up > 0 && <div style={{ height: pct(counts.up), backgroundColor: "#16a34a", borderRadius: counts.down === 0 ? "2px 2px 0 0" : "2px 2px 0 0", transition: "height 0.2s" }} />}
                                    {counts.down > 0 && <div style={{ height: pct(counts.down), backgroundColor: "#dc2626", borderRadius: "0", transition: "height 0.2s" }} />}
                                    {dayTotal === 0 && <div style={{ height: "1px", backgroundColor: "#e5e7eb" }} />}
                                </div>
                            </div>
                        );
                    })}
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginTop: "3px" }}>
                    <span style={{ fontSize: "9px", color: "#9ca3af" }}>{days.length > 0 ? days[0][0].slice(5) : ""}</span>
                    <span style={{ fontSize: "9px", color: "#9ca3af" }}>{days.length > 0 ? days[days.length - 1][0].slice(5) : ""}</span>
                </div>
            </div>
        );
    };

    /* ── Main dashboard ── */
    return (
        <div style={{ overflow: "hidden" }}>
            {/* Header with badges */}
            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "32px" }}>
                <h1 style={{ fontSize: "24px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Dashboard</h1>
                {isAdmin && <Chip label="Admin" size="small" sx={{ backgroundColor: "#f0ebfc", color: "#825ee7", fontWeight: 600, fontSize: "11px" }} />}
            </div>

            {/* Integrations — compact when both connected, full cards otherwise */}
            {bothConnected ? (
                <div style={{ display: "flex", gap: "8px", marginBottom: "24px", flexWrap: "wrap" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e5e7eb", padding: "8px 14px" }}>
                        <HubSpotIcon size={22} />
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1a1a1a" }}>HubSpot</span>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>Portal {hubspotConnectionId}</span>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#16a34a" }} />
                        {isAdmin && <Button size="small" onClick={() => handleDisconnect("hubspot")} sx={{ color: "#dc2626", textTransform: "none", fontSize: "11px", fontWeight: 500, padding: "2px 8px", minWidth: 0, borderRadius: "4px", "&:hover": { backgroundColor: "#fef2f2" } }}>Disconnect</Button>}
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", backgroundColor: "#fff", borderRadius: "8px", border: "1px solid #e5e7eb", padding: "8px 14px" }}>
                        <SlackIcon size={22} />
                        <span style={{ fontSize: "13px", fontWeight: 500, color: "#1a1a1a" }}>Slack</span>
                        <span style={{ fontSize: "12px", color: "#6b7280" }}>{slack.workspace}</span>
                        <span style={{ width: "6px", height: "6px", borderRadius: "50%", backgroundColor: "#16a34a" }} />
                        {isAdmin && <Button size="small" onClick={() => handleDisconnect("slack")} sx={{ color: "#dc2626", textTransform: "none", fontSize: "11px", fontWeight: 500, padding: "2px 8px", minWidth: 0, borderRadius: "4px", "&:hover": { backgroundColor: "#fef2f2" } }}>Disconnect</Button>}
                    </div>
                </div>
            ) : (
                <>
                    <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 16px 0" }}>Integrations</h3>
                    <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: "16px", marginBottom: "32px" }}>
                        <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                                <HubSpotIcon size={32} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>HubSpot</div>
                                    <div style={{ fontSize: "13px", color: "#6b7280" }}>{hubspot.connected ? `Portal ${hubspotConnectionId}` : "CRM & Quoting"}</div>
                                </div>
                                <span style={{ marginLeft: "auto", display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: hubspot.connected ? "#16a34a" : "#d1d5db" }} />
                            </div>
                            {isAdmin && (
                                hubspot.connected ? (
                                    <Button size="small" onClick={() => handleDisconnect("hubspot")} sx={{ color: "#dc2626", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", "&:hover": { backgroundColor: "#fef2f2" } }}>Disconnect</Button>
                                ) : (
                                    <Button size="small" onClick={() => { window.location.href = HUBSPOT_INSTALL_URL; }} sx={{ color: "#825ee7", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #ddd6fe", "&:hover": { backgroundColor: "#f5f3ff" } }}>Connect</Button>
                                )
                            )}
                        </div>
                        <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px solid #e5e7eb", padding: "24px" }}>
                            <div style={{ display: "flex", alignItems: "center", gap: "12px", marginBottom: "16px" }}>
                                <SlackIcon size={32} />
                                <div>
                                    <div style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>Slack</div>
                                    <div style={{ fontSize: "13px", color: "#6b7280" }}>{slack.connected ? slack.workspace : "Messaging"}</div>
                                </div>
                                <span style={{ marginLeft: "auto", display: "inline-block", width: "8px", height: "8px", borderRadius: "50%", backgroundColor: slack.connected ? "#16a34a" : "#d1d5db" }} />
                            </div>
                            {isAdmin && (
                                slack.connected ? (
                                    <Button size="small" onClick={() => handleDisconnect("slack")} sx={{ color: "#dc2626", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", "&:hover": { backgroundColor: "#fef2f2" } }}>Disconnect</Button>
                                ) : (
                                    <Button size="small" onClick={() => { window.location.href = getSlackInstallUrl(); }} sx={{ color: "#825ee7", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #ddd6fe", "&:hover": { backgroundColor: "#f5f3ff" } }}>Connect</Button>
                                )
                            )}
                        </div>
                    </div>
                </>
            )}

            {/* Agent section */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
                <h3 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: 0 }}>Quoting Agents</h3>
                {bothConnected && isAdmin && !showAgentForm && (
                    <Button
                        size="small"
                        startIcon={<AddIcon style={{ fontSize: "16px" }} />}
                        onClick={() => openAgentForm(null, null)}
                        style={{ backgroundColor: "#825ee7", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", padding: "6px 16px", fontSize: "13px" }}
                    >
                        Create Agent
                    </Button>
                )}
            </div>

            {!bothConnected && (
                <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px dashed #d1d5db", padding: "40px", textAlign: "center" }}>
                    <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>Connect both Slack and HubSpot to configure your quoting agents.</p>
                </div>
            )}

            {bothConnected && showAgentForm && isAdmin && renderAgentForm()}

            {bothConnected && agentEntries.length === 0 && !showAgentForm && (
                <div style={{ backgroundColor: "#fff", borderRadius: "12px", border: "1px dashed #d1d5db", padding: "40px", textAlign: "center" }}>
                    {isAdmin ? (
                        <>
                            <h4 style={{ fontSize: "16px", fontWeight: 600, color: "#1a1a1a", margin: "0 0 8px 0" }}>No agents configured</h4>
                            <p style={{ color: "#6b7280", fontSize: "14px", margin: "0 0 20px 0" }}>Create a quoting agent to start handling requests in Slack.</p>
                            <Button variant="contained" onClick={() => openAgentForm(null, null)} style={{ backgroundColor: "#825ee7", color: "#fff", textTransform: "none", fontWeight: 500, borderRadius: "8px", padding: "10px 24px" }}>
                                Create Agent
                            </Button>
                        </>
                    ) : (
                        <p style={{ color: "#9ca3af", fontSize: "14px", margin: 0 }}>No agents configured yet. Your admin will set this up.</p>
                    )}
                </div>
            )}

            {bothConnected && agentEntries.length > 0 && !showAgentForm && (
                <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                    {agentEntries.map(([agentId, agent]) => (
                        <div key={agentId} style={{ backgroundColor: "#fff", borderRadius: "12px", border: agent.enabled ? "2px solid #825ee7" : "1px solid #e5e7eb", padding: isMobile ? "16px" : "24px" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px" }}>
                                <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                                    <div style={{ width: "36px", height: "36px", borderRadius: "8px", backgroundColor: "#f0ebfc", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        <SmartToyIconInline />
                                    </div>
                                    <div>
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            <span style={{ fontWeight: 600, fontSize: "15px", color: "#1a1a1a" }}>{agent.name}</span>
                                            {agent.enabled && <Chip label="Active" size="small" sx={{ backgroundColor: "#f0fdf4", color: "#15803d", fontWeight: 600, fontSize: "10px", height: "20px", border: "1px solid #bbf7d0" }} />}
                                        </div>
                                        <div style={{ fontSize: "13px", color: "#6b7280" }}>{agent.enabled ? "Enabled" : "Disabled"}</div>
                                    </div>
                                </div>
                                {isAdmin && <Switch checked={!!agent.enabled} onChange={() => handleToggleAgent(agentId)} size="small" sx={switchSx} />}
                            </div>
                            {agent.planLineItemsInstructions && (
                                <p style={{ color: "#6b7280", fontSize: "13px", lineHeight: 1.6, margin: "0 0 0 0", display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                    {agent.planLineItemsInstructions}
                                </p>
                            )}
                            {renderInlineStats(agentId)}
                            {isAdmin && (
                                <div style={{ display: "flex", gap: "8px", marginTop: "12px" }}>
                                    <Button size="small" onClick={() => openAgentForm(agentId, agent)} sx={{ color: "#825ee7", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #ddd6fe", "&:hover": { backgroundColor: "#f5f3ff" } }}>Edit</Button>
                                    <Button size="small" onClick={() => handleDeleteAgent(agentId)} sx={{ color: "#dc2626", textTransform: "none", fontSize: "13px", fontWeight: 500, padding: "4px 12px", borderRadius: "6px", border: "1px solid #fecaca", "&:hover": { backgroundColor: "#fef2f2" } }}>Delete</Button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const SmartToyIconInline = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="#825ee7">
        <path d="M20 9V7c0-1.1-.9-2-2-2h-3c0-1.66-1.34-3-3-3S9 3.34 9 5H6c-1.1 0-2 .9-2 2v2c-1.66 0-3 1.34-3 3s1.34 3 3 3v4c0 1.1.9 2 2 2h12c1.1 0 2-.9 2-2v-4c1.66 0 3-1.34 3-3s-1.34-3-3-3zM7.5 11.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5S9.83 13 9 13s-1.5-.67-1.5-1.5zM16 17H8v-2h8v2zm-1-4c-.83 0-1.5-.67-1.5-1.5S14.17 10 15 10s1.5.67 1.5 1.5S15.83 13 15 13z"/>
    </svg>
);


const HubSpotIcon = ({ size = 28 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 489 511.8" fillRule="evenodd" clipRule="evenodd">
        <path fill="#FF7A59" fillRule="nonzero" d="M375.25 168.45V107.5c16.43-7.68 26.97-24.15 27.08-42.29V63.8c0-25.95-21.05-46.99-47-46.99h-1.37c-25.95 0-46.99 21.04-46.99 46.99v1.41a46.985 46.985 0 0027.29 42.3v60.94c-23.13 3.53-44.98 13.18-63.19 27.84L103.88 66.16c1.19-4.29 1.83-8.73 1.89-13.17v-.11C105.77 23.68 82.09 0 52.88 0 23.68 0 0 23.68 0 52.88c0 29.18 23.64 52.85 52.81 52.89 9.17-.08 18.16-2.59 26.06-7.23l164.62 128.07a133.501 133.501 0 00-22.16 73.61c0 27.39 8.46 54.17 24.18 76.58l-50.06 50.06a43.926 43.926 0 00-12.43-1.81c-23.96 0-43.38 19.42-43.38 43.37 0 23.96 19.42 43.38 43.38 43.38 23.95 0 43.37-19.42 43.37-43.38v-.13a41.81 41.81 0 00-2.02-12.5l49.52-49.56a133.687 133.687 0 0081.54 27.78c73.76 0 133.57-59.81 133.57-133.57 0-66.05-48.3-122.2-113.61-132.06l-.14.07zm-20.39 200.4c-36.79-1.52-65.85-31.79-65.85-68.62 0-35.43 26.97-65.06 62.23-68.38h3.62c35.8 2.73 63.46 32.58 63.46 68.48 0 35.91-27.66 65.76-63.45 68.48l-.01.04z"/>
    </svg>
);

const SlackIcon = ({ size = 28 }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 128 128">
        <path fill="#E01E5A" d="M27 80c0 7.7-6.3 14-14 14s-14-6.3-14-14 6.3-14 14-14h14v14zm7 0c0-7.7 6.3-14 14-14s14 6.3 14 14v35c0 7.7-6.3 14-14 14s-14-6.3-14-14V80z"/>
        <path fill="#36C5F0" d="M48 27c-7.7 0-14-6.3-14-14S40.3-1 48-1s14 6.3 14 14v14H48zm0 7c7.7 0 14 6.3 14 14s-6.3 14-14 14H13c-7.7 0-14-6.3-14-14s6.3-14 14-14h35z"/>
        <path fill="#2EB67D" d="M101 48c0-7.7 6.3-14 14-14s14 6.3 14 14-6.3 14-14 14h-14V48zm-7 0c0 7.7-6.3 14-14 14s-14-6.3-14-14V13c0-7.7 6.3-14 14-14s14 6.3 14 14v35z"/>
        <path fill="#ECB22E" d="M80 101c7.7 0 14 6.3 14 14s-6.3 14-14 14-14-6.3-14-14v-14h14zm0-7c-7.7 0-14-6.3-14-14s6.3-14 14-14h35c7.7 0 14 6.3 14 14s-6.3 14-14 14H80z"/>
    </svg>
);

export default Dashboard;
